import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import type { AppTheme } from '@/theme/index';

interface LabeledDateInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  isEdit?: boolean;
  id?: string;
  name?: string;
  min?: string;
  max?: string;
  // 스타일 관련 props
  width?: string;
  labelWidth?: string;
  height?: string;
  fontSize?: string;
  align?: 'left' | 'center' | 'right';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  labelSize?: 'xs' | 'sm' | 'md' | 'lg';
  labelWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  gap?: string;
}

const Container = styled.div<{
  width?: string;
}>`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: ${({ width }) => width || '250px'};
  position: relative;
`;

const LabelButton = styled.div<{
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $weight?: 'normal' | 'medium' | 'semibold' | 'bold';
  $width?: string;
}>`
  width: ${({ $width }) => $width || '70px'};
  height: 26px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  border: ${({ theme }) => theme.borderWidth.md}px solid ${({ theme }) => theme.colors.border.interactive.default};
  border-right: none;
  border-top-left-radius: ${({ theme }) => theme.borderRadius.md}px;
  border-bottom-left-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.emphasis};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.tight};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: nowrap;
  flex-shrink: 0;
  box-sizing: border-box;
`;

const InputWrapper = styled.div`
  flex: 1;
  min-width: 0;
  position: relative;
`;

const StyledDateInput = styled.input<{
  disabled?: boolean;
  theme?: AppTheme;
  $size?: 'xs' | 'sm' | 'md' | 'lg';
  $align?: 'left' | 'center' | 'right';
  $height?: string;
  $fontSize?: string;
}>`
  width: 100%;
  height: 26px;
  padding: ${({ theme }) => `${theme.spacing[2]}px ${theme.spacing[4]}px`};
  border: ${({ theme }) => theme.borderWidth.md}px solid ${({ theme }) => theme.colors.border.interactive.default};
  border-left: none;
  border-top-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  border-bottom-right-radius: ${({ theme }) => theme.borderRadius.md}px;
  background: ${({ theme }) => theme.colors.surface.control.default};
  outline: none;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  font-size: ${({ theme }) => theme.fontSize.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  line-height: ${({ theme }) => theme.lineHeight.normal};
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  box-sizing: border-box;
  transition: border-color 0.2s ease;

  &:focus {
    border-color: ${({ theme }) => theme.colors.border.indicator};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.surface.control.disabled};
    color: ${({ theme }) => theme.colors.text.disabled};
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.border.interactive.hover};
  }

  &::-webkit-datetime-edit {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  }

  &::-webkit-datetime-edit-fields-wrapper {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &::-webkit-calendar-picker-indicator {
    cursor: pointer;
    opacity: 0.6;
    position: absolute;
    right: 8px;
  }

  &::-webkit-calendar-picker-indicator:hover {
    opacity: 1;
  }
`;

export const LabeledDateInput: React.FC<LabeledDateInputProps> = ({
  label,
  value,
  onChange,
  isEdit = true,
  id,
  name,
  min,
  max,
  width,
  labelWidth,
  height,
  fontSize,
  align = 'left',
  size = 'md',
  labelSize = 'sm',
  labelWeight = 'semibold',
  gap: _gap,
}) => {
  const { t } = useTranslation();
  const inputId = id || `labeled-date-${label.replace(/\s+/g, '-').toLowerCase()}`;
  const disabled = !isEdit;

  return (
    <Container width={width}>
      <LabelButton $size={labelSize} $weight={labelWeight} $width={labelWidth}>
        {t(label)}
      </LabelButton>
      <InputWrapper>
        <StyledDateInput
          type="date"
          id={inputId}
          name={name || inputId}
          value={value}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
          min={min}
          max={max}
          $size={size}
          $align={align}
          $height={height}
          $fontSize={fontSize}
        />
      </InputWrapper>
    </Container>
  );
};
